import { IntlShape, MessageDescriptor } from 'react-intl';
import { parseReelsPlanDayKey } from 'fym-shared/src/music24/reelsPlan';
import { ReleasePost } from '../../../../../redux/api/fymAPI.types';
import { platformMeta } from '../../platformMeta';
import { SocialPlatform } from '../../types';
import { PlanPostState } from './planModel';
import { planMessages } from './planMessages';
import { planAssetName, planDayTitle } from './rolloutDayCopy';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Plan tab; each part uses its own slice of it */
import styles from './Plan.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The label of each post state chip (design f1 legend). */
export const POST_STATE_MESSAGES: Record<PlanPostState, MessageDescriptor> = {
  posted: planMessages.statePosted,
  scheduled: planMessages.stateScheduled,
  draft: planMessages.stateDraft,
  needsYou: planMessages.stateNeedsYou,
  failed: planMessages.stateFailed,
  publishing: planMessages.statePublishing,
};

export const POST_STATE_CLASS: Record<PlanPostState, string | undefined> = {
  posted: styles.statePosted,
  scheduled: styles.stateScheduled,
  draft: styles.stateDraft,
  needsYou: styles.stateNeedsYou,
  failed: styles.stateFailed,
  publishing: styles.stateScheduled,
};

/** The name of what a post uses: its asset type, an AI reel, or a plain post. */
export function releasePostName(intl: IntlShape, post: ReleasePost): string {
  if (post.kitAsset !== null) {
    return planAssetName(intl, post.kitAsset.themeId);
  }
  if (post.reel !== null) {
    return planAssetName(intl, 'aiReel');
  }
  return intl.formatMessage(planMessages.textPost);
}

/** The day key of a rollout key (`release.outNow` gives `outNow`); null for a post of no plan. */
export function postDayKey(rolloutKey: string | null): string | null {
  return rolloutKey === null ? null : rolloutKey.slice(rolloutKey.indexOf('.') + 1);
}

/**
 * The step and the asset of a post in one line: "Countdown 7 · Story". A post of an AI reels plan
 * is "AI reel, day 5": the step names the asset already. `asset` is the asset name of the row.
 */
export function postStepLine(intl: IntlShape, post: ReleasePost, asset: string): string {
  const dayKey = postDayKey(post.rolloutKey);
  if (dayKey === null) {
    return asset;
  }
  const step = planDayTitle(intl, { key: dayKey, asset: null });
  return parseReelsPlanDayKey(dayKey) === null ? `${step} · ${asset}` : step;
}

/** The platform name of a post ("Instagram"); the raw value for an unknown platform. */
export function postPlatformName(platform: string): string {
  return platformMeta[platform as SocialPlatform]?.name ?? platform;
}
